/**
 * Internal firm team chat (staff <-> staff, including the manager). Talks to the
 * same `/manager/staff-*` endpoints the Manager app uses, so a message the
 * manager sends from the Manager app lands here in the staff member's panel.
 * These handlers wrap responses as { success, data }, so we read `r.data.data`.
 */
import apiClient from './client';

export interface StaffMessage {
  id: string;
  sender_user_id: string | null;
  sender_name: string | null;
  body: string | null;
  attachment_s3_key?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachmentUrl?: string | null;
  created_at: string;
}

export interface StaffConversation {
  peerUserId: string;
  name: string;
  email?: string | null;
  lastBody: string | null;
  lastAt: string | null;
  unread: number;
}

export interface StaffInbox {
  me: string;
  everyone: { lastBody: string | null; lastAt: string | null; unread: number };
  conversations: StaffConversation[];
  staff: { userId: string; name: string; email?: string | null }[];
}

export interface StaffAttachment { s3Key: string; filename: string; contentType: string }

export async function getStaffInbox(): Promise<StaffInbox> {
  const r = await apiClient.get('/manager/staff-conversations');
  return r.data.data;
}

/** peerUserId omitted => the firm-wide "Everyone" channel. */
export async function getStaffThread(peerUserId?: string): Promise<{ messages: StaffMessage[]; me: string }> {
  const r = await apiClient.get('/manager/staff-messages', { params: peerUserId ? { userId: peerUserId } : {} });
  return r.data.data;
}

export async function sendStaffMessage(input: {
  recipientUserId?: string | null;
  body?: string;
  attachment?: StaffAttachment;
}): Promise<{ message: StaffMessage }> {
  const r = await apiClient.post('/manager/staff-messages', input);
  return r.data.data;
}

/** Presign + PUT a file/voice note to S3, returning the attachment descriptor. */
export async function uploadStaffAttachment(file: Blob, filename: string): Promise<StaffAttachment> {
  const contentType = file.type || 'application/octet-stream';
  const pre = await apiClient.post('/manager/staff-messages', {
    action: 'upload-url', filename, contentType, fileSizeBytes: file.size,
  });
  const { uploadUrl, s3Key } = pre.data.data;
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
  return { s3Key, filename, contentType };
}
